import { useEffect, useRef, useState } from "react";
import {
    composerInsetDurationVariable,
    composerInsetMotion,
    readAuthoredInsetDuration,
    readCssPixel,
} from "../-utils/composerLayoutHeight";

type NativeInsetSample = {
    bottom: number;
    authoredDurationMs: number | null;
};

export type NativeComposerChrome = {
    inset: number | null;
    /** True when the thank-you note should be closed for this inset animation. */
    keyboardOpen: boolean;
};

let pendingNativeKeyboard: boolean | null = null;
const nativeKeyboardListeners = new Set<() => void>();

/**
 * The host reports the keyboard before the list inset eases. Hold the flag
 * until that ease starts so the thank-you note does not collapse first.
 */
export function holdNativeKeyboard(open: boolean) {
    pendingNativeKeyboard = open;
    for (const listener of nativeKeyboardListeners) {
        listener();
    }
}

function consumeNativeKeyboard(): boolean | null {
    const open = pendingNativeKeyboard;
    pendingNativeKeyboard = null;
    return open;
}

/**
 * Bottom coverage the native shell writes into `--safe-area-inset-bottom`.
 * `null` outside the iOS web view so Concierge keeps its own composer height.
 *
 * The shell eases a keyboard change by writing the end inset once, with the
 * keyboard duration. A composer-height jump still eases here when no duration
 * was written.
 */
export function useNativeComposerInset(): NativeComposerChrome {
    const isNative = window.isIosNativeWebView;
    const [sample, setSample] = useState<NativeInsetSample | null>(
        isNative ? { bottom: 0, authoredDurationMs: null } : null,
    );
    const [inset, setInset] = useState<number | null>(isNative ? 0 : null);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const lastTargetRef = useRef(sample?.bottom ?? 0);
    const lastTargetAtRef = useRef(0);
    const commitScheduledRef = useRef(false);
    const lastCommitAtRef = useRef(0);
    const lastDurationRef = useRef(0);

    useEffect(function observeNativeSafeAreaInset() {
        if (!window.isIosNativeWebView) {
            return;
        }

        const root = document.documentElement;

        function readNativeSafeAreaInset() {
            const next = {
                bottom: readCssPixel(
                    root.style.getPropertyValue("--safe-area-inset-bottom"),
                ),
                authoredDurationMs: readAuthoredInsetDuration(
                    root.style.getPropertyValue(composerInsetDurationVariable),
                ),
            };
            setSample((current) => {
                if (current?.bottom === next.bottom) {
                    return current;
                }
                return next;
            });
        }

        readNativeSafeAreaInset();
        const observer = new MutationObserver(readNativeSafeAreaInset);
        observer.observe(root, {
            attributes: true,
            attributeFilter: ["style"],
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(function followHeldNativeKeyboard() {
        let lateFrame = 0;

        function onKeyboardHeld() {
            if (commitScheduledRef.current) {
                return;
            }
            const sinceCommit = performance.now() - lastCommitAtRef.current;
            if (sinceCommit >= lastDurationRef.current) {
                return;
            }
            window.cancelAnimationFrame(lateFrame);
            lateFrame = window.requestAnimationFrame(() => {
                const open = consumeNativeKeyboard();
                if (open !== null) {
                    setKeyboardOpen(open);
                }
            });
        }

        nativeKeyboardListeners.add(onKeyboardHeld);
        return () => {
            nativeKeyboardListeners.delete(onKeyboardHeld);
            window.cancelAnimationFrame(lateFrame);
        };
    }, []);

    useEffect(
        function easeNativeComposerInset() {
            if (!sample) {
                return;
            }

            const target = sample.bottom;
            const now = performance.now();
            const elapsed =
                lastTargetAtRef.current === 0
                    ? Number.POSITIVE_INFINITY
                    : now - lastTargetAtRef.current;
            const step = target - lastTargetRef.current;
            lastTargetAtRef.current = now;
            lastTargetRef.current = target;

            const reduceMotion = window.matchMedia(
                "(prefers-reduced-motion: reduce)",
            ).matches;
            const duration = composerInsetMotion({
                step,
                elapsedMs: elapsed,
                authoredDurationMs: sample.authoredDurationMs,
                reduceMotion,
            });
            lastDurationRef.current = duration;
            const root = document.documentElement;
            console.info(
                `[Albertine] chat safe area bottom target=${target} step=${step} elapsed=${
                    Number.isFinite(elapsed) ? Math.round(elapsed) : "first"
                } duration=${duration}`,
            );
            root.style.setProperty(composerInsetDurationVariable, `${duration}ms`);

            function applyKeyboard() {
                const open = consumeNativeKeyboard();
                if (open !== null) {
                    setKeyboardOpen(open);
                }
            }

            if (duration <= 0) {
                commitScheduledRef.current = false;
                lastCommitAtRef.current = performance.now();
                setInset(target);
                if (Math.abs(step) >= 0.5) {
                    applyKeyboard();
                }
                return;
            }

            commitScheduledRef.current = true;
            // The duration has to be on the element before the height changes,
            // or the browser applies both in one frame and skips the transition.
            // The thank-you note flips in this same commit so its height eases
            // with the list instead of collapsing a frame earlier.
            const frame = window.requestAnimationFrame(() => {
                commitScheduledRef.current = false;
                lastCommitAtRef.current = performance.now();
                setInset(target);
                applyKeyboard();
            });
            return () => {
                commitScheduledRef.current = false;
                window.cancelAnimationFrame(frame);
            };
        },
        [sample],
    );

    return {
        inset: sample ? inset : null,
        keyboardOpen,
    };
}
